/** BC statutory holidays from canada-holidays.ca (cached per year). */

const CANADA_HOLIDAYS_BASE = 'https://canada-holidays.ca/api/v1/holidays';

const cache = new Map();

function holidaysForProvince(body, year, provinceId) {
  const map = new Map();
  (body?.holidays || []).forEach((h) => {
    const date = (h.observedDate || h.date || '').slice(0, 10);
    if (!date.startsWith(String(year))) return;
    const applies = (h.provinces || []).some((p) => p.id === provinceId);
    if (!applies) return;
    if (!map.has(date)) map.set(date, h.nameEn || h.name || 'Holiday');
  });
  return map;
}

export async function fetchProvinceHolidays(year, provinceId = 'BC') {
  const key = `${provinceId}-${year}`;
  if (cache.has(key)) return cache.get(key);

  try {
    const res = await fetch(`${CANADA_HOLIDAYS_BASE}?year=${year}`);
    if (res.ok) {
      const body = await res.json();
      const map = holidaysForProvince(body, year, provinceId);
      cache.set(key, map);
      return map;
    }
  } catch {
    /* fall through */
  }

  try {
    const res = await fetch(CANADA_HOLIDAYS_BASE);
    if (!res.ok) return new Map();
    const body = await res.json();
    const map = holidaysForProvince(body, year, provinceId);
    cache.set(key, map);
    return map;
  } catch {
    return new Map();
  }
}

/** Merge holiday maps for multiple years into one. */
export async function fetchHolidaysForRange(fromIso, toIso, provinceId = 'BC') {
  const fromYear = parseInt(fromIso.slice(0, 4), 10);
  const toYear = parseInt(toIso.slice(0, 4), 10);
  const merged = new Map();
  for (let y = fromYear; y <= toYear; y += 1) {
    const yearMap = await fetchProvinceHolidays(y, provinceId);
    yearMap.forEach((name, date) => {
      if (date >= fromIso && date <= toIso) merged.set(date, name);
    });
  }
  return merged;
}

export function isWeekend(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  return dow === 0 || dow === 6;
}

export function getForecastDayKind(isoDate, holidayMap) {
  const holidayName = holidayMap?.get(isoDate);
  if (holidayName) return { kind: 'holiday', holidayName };
  if (isWeekend(isoDate)) return { kind: 'weekend' };
  return { kind: 'weekday' };
}

/**
 * Fetch canada-holidays.ca without using module cache (Debug / ops checks).
 * Response shape mirrors what fetchProvinceHolidays relies on after success.
 *
 * @param {number|string} year
 * @param {string} [provinceId='BC']
 * @returns {Promise<{
 *   ok: boolean,
 *   provinceId: string,
 *   year: number,
 *   requests: Array<object>,
 *   holidayDatesForProvinceYear: Array<{ date: string, name: string }>,
 *   error?: string,
 * }>}
 */
export async function probeCanadaHolidaysApi(year, provinceId = 'BC') {
  const y = Number(year);
  const safeYear = Number.isFinite(y) && y >= 1900 && y <= 2200 ? y : new Date().getFullYear();

  /** @type {{ ok: boolean, provinceId: string, year: number, requests: object[], holidayDatesForProvinceYear: Array<{ date: string, name: string }>, error?: string }} */
  const out = {
    ok: false,
    provinceId,
    year: safeYear,
    requests: [],
    holidayDatesForProvinceYear: [],
  };

  /** @param {string} url @param {string} label */
  async function attempt(url, label) {
    const req = {
      label,
      url,
      ok: false,
      status: null,
      statusText: '',
      parsedJson: false,
      holidaysArrayLength: null,
      provinceHolidayDatesForRequestedYear: 0,
      bodySnippet: null,
      parseError: null,
      networkError: null,
    };
    try {
      const res = await fetch(url);
      req.ok = res.ok;
      req.status = res.status;
      req.statusText = res.statusText;
      const text = await res.text();
      req.bodySnippet = text.length > 4000 ? `${text.slice(0, 4000)}…` : text;

      if (res.ok) {
        try {
          const body = JSON.parse(text);
          req.parsedJson = true;
          req.holidaysArrayLength = Array.isArray(body?.holidays) ? body.holidays.length : null;
          const map = holidaysForProvince(body, safeYear, provinceId);
          req.provinceHolidayDatesForRequestedYear = map.size;
          out.holidayDatesForProvinceYear = [...map.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, name]) => ({ date, name }));
          out.ok = true;
        } catch (e) {
          req.parseError = String(e.message || e);
        }
      }
    } catch (e) {
      req.networkError = String(e.message || e);
    }
    out.requests.push(req);
    return req;
  }

  const primaryUrl = `${CANADA_HOLIDAYS_BASE}?year=${safeYear}`;
  const r1 = await attempt(primaryUrl, 'Primary (?year)');
  if (!out.ok) {
    await attempt(CANADA_HOLIDAYS_BASE, 'Fallback (no year param)');
  }
  if (!out.ok && !out.error) {
    out.error =
      r1.networkError ||
      (!r1.parsedJson && r1.bodySnippet ? 'No valid JSON from primary URL' : 'Could not resolve holidays for province/year');
  }
  return out;
}
